// GeoNames gazetteer (PLAN §3 "Geocoding", §9 Map; task 30): open data imported into D1, so place look-ups never
// leave Hoopoe. This module is pure: it parses the GeoNames dump files (cities with population ≥ 15,000, first-level
// administrative divisions, countries) into the rows the D1 tables hold, and folds names the same way everywhere
// (the loader when writing, the API when searching). Loading and searching are in @hoopoe/db (gazetteer.ts).

import { detectScript, type NameScript, normalizeName } from './names';

/** The GeoNames extract loaded into D1. Changing it (or the row shape) reloads the table on the next deploy. */
export const GAZETTEER_VERSION = 'cities15000-v1';

/** Base URL of the GeoNames dump (open data, CC BY 4.0). */
export const GEONAMES_DUMP = 'https://download.geonames.org/export/dump';

export const GAZETTEER_KINDS = ['country', 'capital', 'city'] as const;
export type GazetteerKind = (typeof GAZETTEER_KINDS)[number];

/** One gazetteer entry as the API returns it. */
export interface GazetteerHit {
  geonameId: number;
  name: string;
  asciiName: string;
  lat: number;
  lng: number;
  kind: GazetteerKind;
  /** GeoNames feature code: PPLC (capital), PPLA (region seat), PPL (city), PCLI (country)… */
  featureCode: string;
  countryCode: string;
  countryName: string;
  /** First-level division (region, state), when known. */
  admin1: string | null;
  population: number;
}

/** A gazetteer row ready to load: the hit plus every folded name it can be found by. */
export interface GazetteerRow extends GazetteerHit {
  names: string[];
}

/** Folded form used to index and search gazetteer names (the same folding as entity names). */
export const foldPlaceName = (s: string) => normalizeName(s);

/**
 * Alternate names kept per place and script (after dropping duplicates and machine romanizations): GeoNames lists
 * them alphabetically, so a single cap would keep only Latin spellings and lose the Arabic or Hebrew name.
 */
export const MAX_ALT_NAMES: Partial<Record<NameScript, number>> & { other: number } = { Latn: 80, other: 10 };

/** Scripts whose alternate names are kept: the scripts Hoopoe's data is written in. */
const KEPT_SCRIPT = /^[\p{Script=Latin}\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Cyrillic}\p{Script=Greek}\p{N}\p{P}\p{Zs}'’ʼ]+$/u;
/** GeoNames lists lower-case romanizations of names in other scripts ("aisakaladesa-inagoradani"): not useful here. */
const ROMANIZATION = /^[\p{Ll}\p{N}\p{P}\p{Zs}'’ʼ=]+$/u;

export interface CityLine {
  geonameId: number;
  name: string;
  asciiName: string;
  alternateNames: string[];
  lat: number;
  lng: number;
  featureClass: string;
  featureCode: string;
  countryCode: string;
  admin1Code: string;
  population: number;
}

const lines = (tsv: string) => tsv.split(/\r?\n/).filter(l => l && !l.startsWith('#'));

/** `cities15000.txt` (the GeoNames "geoname" table format, tab-separated, 19 columns). Malformed lines are skipped. */
export function parseCities(tsv: string): CityLine[] {
  const out: CityLine[] = [];
  for (const line of lines(tsv)) {
    const c = line.split('\t');
    if (c.length < 15) continue;
    const geonameId = Number(c[0]);
    const lat = Number(c[4]);
    const lng = Number(c[5]);
    if (!Number.isInteger(geonameId) || !Number.isFinite(lat) || !Number.isFinite(lng) || !c[1]) continue;
    out.push({
      geonameId,
      name: c[1] ?? '',
      asciiName: c[2] || c[1] || '',
      alternateNames: (c[3] ?? '').split(',').filter(Boolean),
      lat,
      lng,
      featureClass: c[6] ?? '',
      featureCode: c[7] ?? '',
      countryCode: c[8] ?? '',
      admin1Code: c[10] ?? '',
      population: Number(c[14]) || 0,
    });
  }
  return out;
}

/** `admin1CodesASCII.txt`: `FR.11 → Île-de-France`. */
export function parseAdmin1(tsv: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const line of lines(tsv)) {
    const [code, name] = line.split('\t');
    if (code && name) out.set(code, name);
  }
  return out;
}

export interface CountryLine {
  code: string;
  name: string;
  geonameId: number;
  population: number;
}

/** `countryInfo.txt`: ISO code, name, population and the country's GeoNames id. */
export function parseCountries(tsv: string): CountryLine[] {
  const out: CountryLine[] = [];
  for (const line of lines(tsv)) {
    const c = line.split('\t');
    const geonameId = Number(c[16]);
    if (!c[0] || !c[4] || !Number.isInteger(geonameId) || geonameId <= 0) continue;
    out.push({ code: c[0], name: c[4], geonameId, population: Number(c[7]) || 0 });
  }
  return out;
}

/** Middle of the 10th–90th percentile range: inside the country's main territory, not pulled to its largest region. */
const middle = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const at = (p: number) => s[Math.min(s.length - 1, Math.floor(p * (s.length - 1)))] as number;
  return (at(0.1) + at(0.9)) / 2;
};

const round = (n: number) => Math.round(n * 1e5) / 1e5;

/** Folded, unique, searchable names: the name, its ASCII form, then alternates in kept scripts (romanizations dropped). */
export function placeNames(name: string, asciiName: string, alternates: readonly string[]): string[] {
  const out = new Set<string>();
  for (const n of [name, asciiName]) {
    const f = foldPlaceName(n);
    if (f) out.add(f);
  }
  const perScript = new Map<NameScript, number>();
  for (const a of alternates) {
    const t = a.trim();
    if (!t || t.length > 80 || !KEPT_SCRIPT.test(t) || ROMANIZATION.test(t)) continue;
    const script = detectScript(t);
    const used = perScript.get(script) ?? 0;
    if (used >= (MAX_ALT_NAMES[script] ?? MAX_ALT_NAMES.other)) continue;
    const f = foldPlaceName(t);
    if (!f || out.has(f)) continue;
    out.add(f);
    perScript.set(script, used + 1);
  }
  return [...out];
}

export interface BuildInput {
  cities: CityLine[];
  admin1: Map<string, string>;
  countries: CountryLine[];
  /** Country names in other languages (e.g. from Intl.DisplayNames): ISO code → names. */
  countryNames?: (code: string) => string[];
}

/**
 * The rows to load: every city (capitals marked), plus one row per country placed in the middle of its cities (a
 * label point inside the country, not its capital). Countries without any city in the extract are left out.
 */
export function buildGazetteer(input: BuildInput): GazetteerRow[] {
  const countryName = new Map(input.countries.map(c => [c.code, c.name]));
  const rows: GazetteerRow[] = [];
  const byCountry = new Map<string, CityLine[]>();
  for (const c of input.cities) {
    if (c.featureClass !== 'P') continue;
    const list = byCountry.get(c.countryCode) ?? [];
    list.push(c);
    byCountry.set(c.countryCode, list);
    rows.push({
      geonameId: c.geonameId,
      name: c.name,
      asciiName: c.asciiName,
      lat: round(c.lat),
      lng: round(c.lng),
      kind: c.featureCode === 'PPLC' ? 'capital' : 'city',
      featureCode: c.featureCode,
      countryCode: c.countryCode,
      countryName: countryName.get(c.countryCode) ?? c.countryCode,
      admin1: input.admin1.get(`${c.countryCode}.${c.admin1Code}`) ?? null,
      population: c.population,
      names: placeNames(c.name, c.asciiName, c.alternateNames),
    });
  }
  for (const country of input.countries) {
    const cities = byCountry.get(country.code);
    if (!cities?.length) continue;
    rows.push({
      geonameId: country.geonameId,
      name: country.name,
      asciiName: country.name,
      lat: round(middle(cities.map(c => c.lat))),
      lng: round(middle(cities.map(c => c.lng))),
      kind: 'country',
      featureCode: 'PCLI',
      countryCode: country.code,
      countryName: country.name,
      admin1: null,
      population: country.population,
      names: placeNames(country.name, country.name, [country.code, ...(input.countryNames?.(country.code) ?? [])]),
    });
  }
  return rows;
}

/** "Paris, Île-de-France, France" (a country is just its name). */
export function gazetteerLabel(h: Pick<GazetteerHit, 'name' | 'kind' | 'admin1' | 'countryName'>): string {
  if (h.kind === 'country') return h.name;
  return [h.name, h.admin1 && h.admin1 !== h.name ? h.admin1 : null, h.countryName].filter(Boolean).join(', ');
}

export const GAZETTEER_KIND_LABELS: Record<GazetteerKind, string> = { country: 'Country', capital: 'Capital city', city: 'City' };
